import clsx from 'clsx';
import { ShopperProductsTypes } from 'commerce-sdk-isomorphic';
import { useEffect, useState } from 'react';
import {
    PrimaryProductAction,
    SecondaryProductAction,
} from '~/features/product-details/components/ProductActions/ProductActions';
import { usePDPState } from '~/features/product-details/context';
import { DynamicImageLoader } from '~/shared/components/DynamicImageLoader';
import { useCountdownTheme } from '~/shared/hooks/countdown';
import { ProductStickyCountdown } from './ProductStickyCountdown';
import styles from './ProductSticky.module.scss';

type Props = React.HTMLAttributes<HTMLDivElement> & {
    image: ShopperProductsTypes.Image | undefined;
    referencePoint?: React.RefObject<HTMLDivElement>;
};

export const ProductSticky: React.FC<Props> = ({ className, image, referencePoint, ...rest }) => {
    const [isVisible, setIsVisible] = useState(false);
    const { isPrelaunchCountdownVisible, isPrelaunchBlockingBuy } = usePDPState();
    const countdownTheme = useCountdownTheme();

    useEffect(() => {
        const element = referencePoint?.current;

        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                // 1. Ensure it is completely out of view.
                // 2. Ensure it is above the top of the viewport.
                const isPassedAndHidden = !entry.isIntersecting && entry.boundingClientRect.top < 0;

                setIsVisible(isPassedAndHidden);
            },
            { threshold: 0 }, // Triggers immediately when the element fully enters or leaves
        );

        observer.observe(element);

        return () => observer.disconnect();
    }, [referencePoint]);

    return (
        <div
            className={clsx(styles.productSticky, isVisible && styles.isVisible, className)}
            {...rest}
        >
            <div
                className={clsx(
                    styles.productStickyBox,
                    isPrelaunchCountdownVisible && styles.isPrelaunch,
                    isPrelaunchBlockingBuy && styles.isBlockingBuy,
                )}
                style={isPrelaunchCountdownVisible ? countdownTheme : undefined}
            >
                {/* The countdown takes the image's place rather than sitting beside it. */}
                {!isPrelaunchCountdownVisible && image?.link && (
                    <DynamicImageLoader
                        className={styles.image}
                        width={56}
                        height={56}
                        src={image.link}
                        alt={image.alt ?? image.title ?? ''}
                    />
                )}

                {isPrelaunchCountdownVisible && (
                    <ProductStickyCountdown className={styles.countdown} isPaused={!isVisible} />
                )}

                <PrimaryProductAction
                    containerClassName={styles.buttonContainer}
                    className={clsx(styles.button, styles.primaryButton)}
                    footnote={false}
                    condensed
                />

                <SecondaryProductAction
                    containerClassName={styles.buttonContainer}
                    className={styles.button}
                    footnote={false}
                    condensed
                />
            </div>
        </div>
    );
};
